import React, { useEffect, useState } from "react";

import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button, Table, Popconfirm, notification, Tag, Spin, Dropdown, Modal,
} from "antd";

import logout from "../../api/logout";
import {
  getUserConfiguration,
  manageUserConfiguration,
  getAllCompanies,
  updateCompanyAccess,
  deleteSupportUser,
} from "../../api/userConfig";
import UserModal from "./UserModal";

import { signOut, getUserCredentials } from "../../utils/common";

import "./style.css";

notification.config({
  duration: 3,
  placement: "topRight",
  showProgress: true,
  pauseOnHover: true,
  rtl: false,
});

const UserConfiguration = (routeProps) => {
  const { history } = routeProps;
  const [userData, setUserData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingName, setDeletingName] = useState("");
  const [openSignOut, setOpenSignOut] = useState(false);

  const fetchCompanyList = async () => {
    try {
      const data = await getAllCompanies();
      let options = [];

      if (Array.isArray(data) && data.length > 0) {
        if (typeof data[0] === "string") {
          options = [
            {
              label: "Companies",
              options: data.map((name) => ({
                label: name,
                value: name,
              })),
            },
          ];
        } else if (data[0] && typeof data[0] === "object") {
          const groupedCompanies = data.reduce((acc, company) => {
            const domain = company.company_domain || "Companies";
            if (!acc[domain]) {
              acc[domain] = [];
            }
            const names = Array.isArray(company.company_name)
              ? company.company_name
              : [company.company_name];
            acc[domain].push(...names.filter(Boolean));
            return acc;
          }, {});

          options = Object.keys(groupedCompanies).map((domain) => ({
            label: domain,
            options: groupedCompanies[domain].map((name) => ({
              label: name,
              value: name,
            })),
          }));
        }
      }

      setCompanyList(options);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message ?? "Failed to fetch user data.",
      });
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await getUserConfiguration();
      setUserData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notification.error({
        message: "Error",
        description: error?.response?.data?.message ?? "Failed to fetch user data.",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = async (record) => {
    setLoading(true);
    await fetchCompanyList();
    setCurrentUser({
      ...record,
      company_name: record.company_name,
      original_user: record.user,
    });
    setLoading(false);
    setIsAdding(false);
    setModalVisible(true);
  };

  // Handle user deletion
  const handleDelete = async (user) => {
    try {
      setDeletingName(user.user);
      await deleteSupportUser(user.user);
      notification.success({
        message: "Success",
        description: "User deleted successfully.",
      });
      setDeletingName("");
      fetchData();
    } catch (error) {
      setDeletingName("");
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Failed to delete user.",
      });
    }
  };

  // Handle user addition or editing
  const handleModalSubmit = async (values) => {
    try {
      if (isAdding) {
        const payload = {
          user: values.user,
          ...(values.password && { password: values.password }),
          company_access: values.company_access || values.company_name || [],
        };
        await manageUserConfiguration(payload);
      } else {
        const payload = {
          user: currentUser?.user || values.user,
          company_access: values.company_access || values.company_name || [],
        };
        await updateCompanyAccess(payload);
      }
      notification.success({
        message: "Success",
        description: `User ${isAdding ? "added" : "updated"} successfully.`,
      });
      setModalVisible(false);
      setCompanyList([]);
      setCurrentUser(null);
      fetchData();
      return true;
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Operation failed.",
      });
      return false;
    }
  };

  const columns = [
    {
      title: "Users",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Companies",
      dataIndex: "company_name",
      key: "company_name",
      render: (companies) => (
        <>
          <div className="company_list">
            {companies.map((company) => (
              <Tag color="blue" key={company}>
                {company.trim() === "all" ? "All Companies" : company.trim()}
              </Tag>
            ))}
          </div>
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <div className="action-buttons">
            <Button
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
              placement="topRight"
            >
              <Button danger loading={deletingName === record.user}>
                Delete
              </Button>
            </Popconfirm>
          </div>
        </>
      ),
    },
  ];

  const handleSignOut = async () => {
    // Implement sign-out logic here
    setOpenSignOut(false);
    logout().catch(() => {
      // Silently handle errors - will still clear storage
    });
    const appContext = {
      setUserTitle: () => {}, // Placeholder
    };
    signOut(history, appContext);
  };

  const items = [
    {
      label: "Sign Out",
      key: "1",
      onClick: () => setOpenSignOut(true),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="user-configuration-header">
        <h1 className="title">User Configuration</h1>
        <div className="actions-container">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              fetchCompanyList();
              setIsAdding(true);
              setModalVisible(true);
              setCurrentUser(null);
            }}
          >
            Add User
          </Button>
          <Dropdown menu={{ items }} placement="bottomRight">
            <Button icon={<UserOutlined />}>{`Hi, ${getUserCredentials()?.user_name}`}</Button>
          </Dropdown>
        </div>
      </div>
      <div className="user-configuration">
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={userData.map((item, index) => ({ ...item, key: index }))}
            pagination={false}
            bordered
          />
        </div>
        {modalVisible && (
        <UserModal
          open={modalVisible}
          onClose={() => setModalVisible(false)}
          onSubmit={handleModalSubmit}
          initialValues={isAdding ? {} : currentUser}
          isAdding={isAdding}
          companyList={companyList}
        />
        )}
      </div>
      <Modal
        centered
        title="Sign Out"
        open={openSignOut}
        onOk={handleSignOut}
        okText="Sign Out"
        onCancel={() => setOpenSignOut(false)}
      >
        Are you sure you want to sign out?
      </Modal>
    </Spin>
  );
};

export default UserConfiguration;
